// Angular imports
import { Component, OnInit, ViewChild, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// 3rd Party imports
import { Subscription } from 'rxjs';

// CC-Common imports
import { CCLocalStorageService, Utils } from 'cc-common';

// Component imports
import { MPBaseComponentWithGrid } from '../../../_base/mp-base-component-with-grid';
import { WorkflowQueueDataGridComponent } from '../workflow-queue-datagrid/workflow-queue-datagrid.component';
import { LiteModePreferenceComponent } from '../../../shared-module/lite-mode-preference/lite-mode-preference.component';

// Service imports
import { WorkflowQueueService } from '../../_services/workflow-queue.service';
import { WorkflowEditorService } from '../../../workflow/_services/workflow-editor.service';
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { UserService } from '../../../_services/data-services/user.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';

// Models imports
import { WorkFlowQueueParametersDto } from '../../_models/workflow-queue/workflow-queue-parameters-dto';
import { WorkflowQueueResultDto } from '../../_models/workflow-queue/workflow-queue-result-dto';

// Constant imports
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';

@Component({
    selector: 'app-workflow-queue',
    templateUrl: './workflow-queue.component.html',
    styleUrls: ['./workflow-queue.component.scss']
})
export class WorkflowQueueComponent extends MPBaseComponentWithGrid implements OnInit, OnDestroy {
    @ViewChild('workflowGrid', { static: false }) workflowGrid: WorkflowQueueDataGridComponent;
    @ViewChild('liteModePreference', { static: false }) liteModePreference: LiteModePreferenceComponent;
    @Input() queueStatus: string;
    @Input() queueWorkBy: string;

    queueViewOptions: Array<any> = [];
    selectedQueueViewOptionId: number;
    workflowQueueDataSource: WorkflowQueueResultDto[];
    selectedQueueViewOption: any;
    isSmallScreen: boolean;
    loadingVisible: boolean;

    isQueueOptionDisabled = false;
    queueFilters: any[];
    queueReloadSubscription: Subscription;

    isLiteMode: boolean;
    helpLink: string;

    constructor(
        protected readonly screenService: ScreenService,
        protected readonly localStorageService: CCLocalStorageService,
        protected readonly helperService: HelperService,
        private readonly errorService: ServerErrorService,
        private readonly workflowQueueService: WorkflowQueueService,
        private readonly workflowEditorService: WorkflowEditorService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly ciraHelpService: CiraHelpService
    ) {
        super(screenService, localStorageService, helperService);
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.aapWfQueue);
    }

    ngOnInit() {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => {
            this.isSmallScreen = this.screenService.isSmallScreen();
        });

        this.queueReloadSubscription = this.workflowEditorService
            .getSubjectRefreshWorkflow()
            .subscribe((result: boolean) => {
                if (Utils.isTrue(result)) {
                    this.getWorkflowQueueData(true);
                }
            });

        this.queueViewOptions = [
            {
                id: 1,
                text: 'Show My Work',
                name: 'my',
                includeAll: false
            },
            {
                id: 2,
                text: 'Show ALL Work',
                name: 'all',
                includeAll: true
            }
        ];

        if (this.queueStatus) {
            this.getSelectedQueueViewOptionItemByName();
            this.getWorkflowQueueData(true);
        }

        this.activatedRoute.params.subscribe((param) => {
            this.queueStatus = param.status;
            this.queueWorkBy = param.work;
            if (this.queueStatus) {
                let routeURL = this.router.url;
                routeURL = routeURL.replace(this.queueStatus + '/', '');
                routeURL = routeURL.replace(this.queueWorkBy + '/', '');
                this.helperService.changeNavigation(routeURL);
            }

            this.getSelectedQueueViewOptionItemByName();
            this.getWorkflowQueueData(true);
        });
    }

    ngOnDestroy(): void {
        if (!!this.queueReloadSubscription) {
            this.queueReloadSubscription.unsubscribe();
        }
    }

    onRefreshInboxClick(): void {
        this.getWorkflowQueueData(true);
    }

    onQueueViewOptionValueChange(): void {
        this.getSelectedQueueViewOptionItem();
    }

    onLiteModeValueChange(): void {
        this.liteModePreference.show(this.isLiteMode);
    }

    onLiteModePreferenceChange(): void {
        this.getWorkflowQueueData();
    }

    setStoredLiteMode(flag: boolean): void {
        setTimeout(() => {
            this.isLiteMode = flag;
            this.getWorkflowQueueData(true);
        }, 200);
    }

    onForceRefresh(forceReaload: boolean): void {
        if (Utils.isTrue(forceReaload)) {
            this.getWorkflowQueueData(true);
        }
    }

    isLiteModeDefined(): boolean {
        if (this.isLiteMode === true || this.isLiteMode === false) {
            return true;
        } else {
            return false;
        }
    }

    private getSelectedQueueViewOptionItem(): void {
        if (this.selectedQueueViewOptionId) {
            const ele = this.queueViewOptions.find((opt) => opt.id === this.selectedQueueViewOptionId);
            if (!!ele) {
                this.selectedQueueViewOption = ele;
            }
        } else {
            this.selectedQueueViewOption = null;
        }

        this.getWorkflowQueueData();
    }

    private getSelectedQueueViewOptionItemByName(): void {
        if (this.queueWorkBy) {
            const ele = this.queueViewOptions.find((opt) => opt.name === this.queueWorkBy.toLowerCase());
            if (!!ele) {
                this.selectedQueueViewOption = ele;
                this.selectedQueueViewOptionId = ele.id;
                this.isQueueOptionDisabled = true;
            }
        } else {
            this.selectedQueueViewOptionId = 2;
            this.selectedQueueViewOption = this.queueViewOptions[1];
        }
    }

    private getWorkflowQueueData(clearCache = false): void {
        const baseParams: WorkFlowQueueParametersDto = new WorkFlowQueueParametersDto();
        baseParams.includeAll = this.selectedQueueViewOption.includeAll;
        baseParams.clearCache = clearCache;
        baseParams.liteMode = this.isLiteMode;

        if (this.isLiteModeDefined()) {
            this.loadingVisible = true;

            this.workflowQueueService.getWorkflowQueue(baseParams).subscribe(
                (result: WorkflowQueueResultDto[]) => {
                    this.workflowQueueDataSource = result;
                    this.queueFilters = [];
                    if (Utils.notNullOrEmpty(this.queueStatus)) {
                        this.queueFilters.push(['status', '=', this.queueStatus]);
                    }
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }
}
